import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { BadgeCheck, Loader2, Star, ThumbsUp } from 'lucide-react';
import type { ConceptReviewsResponse } from '@/services/api';

/** Review text arrives with the odd HTML line break from the source. */
function plain(text: string): string {
  return text.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

/** The Amazon reviews behind one buyer concept, best match first. Admin review only. */
export function ConceptReviews({
  load,
  deps,
}: {
  load: () => Promise<ConceptReviewsResponse>;
  deps: unknown[];
}) {
  const [data, setData] = useState<ConceptReviewsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setData(null);
    setError(null);
    setShowAll(false);
    load()
      .then(setData)
      .catch((e: Error) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  if (error) return <div className="text-xs text-red-700">{error}</div>;
  if (!data) {
    return (
      <div className="flex items-center text-xs text-slate-400">
        <Loader2 size={12} className="animate-spin mr-2" /> Loading reviews…
      </div>
    );
  }
  if (!data.available) {
    return (
      <div className="text-xs text-slate-500">
        No review index is published for this version yet, so only the quoted review above is available.
      </div>
    );
  }
  if (data.reviews.length === 0) {
    return <div className="text-xs text-slate-500">No reviews were linked to this concept.</div>;
  }
  const shown = showAll ? data.reviews : data.reviews.slice(0, 6);
  const clients = new Set(data.reviews.map((r) => r.client_uid));
  return (
    <div className="space-y-2">
      <div className="text-xs text-slate-500">
        {data.method === 'cluster'
          ? `${data.total.toLocaleString()} reviews in the cluster this concept was mined from`
          : `${data.total.toLocaleString()} reviews linked by similarity`}
        {data.reviews.length < data.total && `; the ${data.reviews.length} closest to the concept shown`}
        {clients.size > 1 && ` · from ${[...clients].join(', ')}`}
      </div>
      <ul className="space-y-2">
        {shown.map((r) => (
          <li key={`${r.client_uid}:${r.review_id}`} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-2 text-xs">
              {r.rating != null && (
                <span className="inline-flex items-center gap-0.5 text-amber-500" title={`${r.rating} of 5`}>
                  {Array.from({ length: 5 }, (_, i) => (
                    <Star key={i} size={11} className={clsx(i < (r.rating ?? 0) ? 'fill-current' : 'text-slate-200')} />
                  ))}
                </span>
              )}
              {r.title && <span className="font-medium text-navy-900 truncate">{r.title}</span>}
              {r.verified_purchase && (
                <span className="inline-flex items-center gap-0.5 text-emerald-700" title="Verified purchase">
                  <BadgeCheck size={11} /> verified
                </span>
              )}
              {!!r.helpful_votes && (
                <span className="inline-flex items-center gap-0.5 text-slate-400">
                  <ThumbsUp size={10} /> {r.helpful_votes}
                </span>
              )}
              {r.similarity != null && (
                <span className="ml-auto tabular-nums text-slate-400" title="Similarity to the concept">
                  {r.similarity.toFixed(2)}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-slate-700 leading-snug">{plain(r.text)}</p>
            {(r.product_title || r.rung) && (
              <div className="mt-1 text-[11px] text-slate-400 truncate">
                {r.product_title}
                {r.rung && ` · ${r.rung} shelf`}
                {clients.size > 1 && ` · ${r.client_uid}`}
              </div>
            )}
          </li>
        ))}
      </ul>
      {data.reviews.length > shown.length && (
        <button type="button" className="text-xs text-navy-700 underline" onClick={() => setShowAll(true)}>
          Show all {data.reviews.length}
        </button>
      )}
    </div>
  );
}
